import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import {
  KpiStrip,
  SelectDropdown,
  DataTable,
  MetricCard
} from '../../components';
import { usePrimeursRecap } from 'apiTyped/hooks/primeurs.query';
import type { SelectOption } from '../../components';

export const PrimeursRecapTab: React.FC = () => {
  const [wine, setWine] = useState<string>('');
  const [vintage, setVintage] = useState<string>('');

  const { isLoading, recap, client_wines } = usePrimeursRecap(
    vintage ? { vintage } : undefined
  );

  const histoPrice = recap?.histo_price || [];
  const kpis = recap?.kpis || [];
  const storeStats = recap?.store_stats || [];

  // Build filter options
  const wineOptions: SelectOption[] = useMemo(() => {
    const names = [
      ...new Set(client_wines.map((cw: any) => cw.wine || cw.wine_name))
    ].filter(Boolean);
    return [
      { value: '', label: 'All wines' },
      ...names.map((n) => ({ value: n as string, label: n as string }))
    ];
  }, [client_wines]);

  const vintageOptions: SelectOption[] = useMemo(() => {
    const vintages: string[] = Array.from(
      new Set(client_wines.map((cw: any) => String(cw.vintage)))
    ) as string[];
    vintages.sort().reverse();
    return [
      { value: '', label: 'All vintages' },
      ...vintages.map((v) => ({ value: v, label: v }))
    ];
  }, [client_wines]);

  // KPI for selected wine/vintage
  const activeKpi = useMemo(() => {
    if (!kpis || kpis.length === 0) return null;
    let filtered = [...kpis];
    if (wine) filtered = filtered.filter((k: any) => k.wine_name === wine);
    if (vintage)
      filtered = filtered.filter((k: any) => String(k.vintage) === vintage);
    return filtered.length > 0 ? filtered[0] : kpis[0];
  }, [kpis, wine, vintage]);

  const kpiItems = useMemo(() => {
    if (!activeKpi) {
      return [
        { label: 'PMM Alerts', value: '—', unavailable: true },
        { label: 'Lowest margin store', value: '—', unavailable: true },
        { label: 'Max sellers/bottle', value: '—', unavailable: true },
        { label: 'Total sellers', value: '—', unavailable: true }
      ];
    }
    return [
      { label: 'PMM Alerts', value: String(activeKpi.total_pmm_alerts || 0) },
      {
        label: 'Lowest margin store',
        value: activeKpi.lowest_margin_store_name || '—'
      },
      {
        label: 'Max sellers/bottle',
        value: String(activeKpi.max_sellers_bottle || 0)
      },
      { label: 'Total sellers', value: String(activeKpi.max_sellers_all || 0) }
    ];
  }, [activeKpi]);

  // Filtered history data
  const filteredHistory = useMemo(() => {
    let data = [...histoPrice];
    if (wine) data = data.filter((d: any) => d.wine_name === wine);
    if (vintage) data = data.filter((d: any) => String(d.vintage) === vintage);
    return data as any[];
  }, [histoPrice, wine, vintage]);

  // Filtered store stats
  const filteredStores = useMemo(() => {
    let data = [...storeStats];
    if (wine) data = data.filter((d: any) => d.wine_name === wine);
    if (vintage) data = data.filter((d: any) => String(d.vintage) === vintage);
    return data as any[];
  }, [storeStats, wine, vintage]);

  const historyColumns = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        render: (row: any) => row.date || '—',
        width: '12%'
      },
      {
        key: 'wine_name',
        header: 'Wine',
        render: (row: any) => row.wine_name,
        width: '25%'
      },
      {
        key: 'vintage',
        header: 'Vintage',
        render: (row: any) => row.vintage,
        width: '10%'
      },
      {
        key: 'min_price',
        header: 'Min Price',
        render: (row: any) =>
          row.min_euro_price != null
            ? `${Number(row.min_euro_price).toFixed(0)}€`
            : '—',
        width: '13%'
      },
      {
        key: 'avg_price',
        header: 'Avg Price',
        render: (row: any) =>
          row.avg_euro_price != null
            ? `${Number(row.avg_euro_price).toFixed(0)}€`
            : '—',
        width: '13%'
      },
      {
        key: 'max_price',
        header: 'Max Price',
        render: (row: any) =>
          row.max_euro_price != null
            ? `${Number(row.max_euro_price).toFixed(0)}€`
            : '—',
        width: '13%'
      },
      {
        key: 'nb_sellers',
        header: 'Sellers',
        render: (row: any) =>
          row.nb_sellers != null ? String(row.nb_sellers) : '—',
        width: '10%'
      }
    ],
    []
  );

  const storeColumns = useMemo(
    () => [
      {
        key: 'store_name',
        header: 'Store',
        render: (row: any) => row.store_name || '—',
        width: '25%'
      },
      {
        key: 'wine_name',
        header: 'Wine',
        render: (row: any) => row.wine_name || '—',
        width: '20%'
      },
      {
        key: 'price',
        header: 'Price',
        render: (row: any) =>
          row.ht_euro_price != null
            ? `${Number(row.ht_euro_price).toFixed(0)}€`
            : '—',
        width: '12%'
      },
      {
        key: 'margin',
        header: 'Margin',
        render: (row: any) => {
          if (row.margin == null) return '—';
          const val = Number(row.margin);
          return <MarginCell $low={val < 15}>{val.toFixed(1)}%</MarginCell>;
        },
        width: '12%'
      },
      {
        key: 'market',
        header: 'Market',
        render: (row: any) => row.store_market || row.market || '—',
        width: '15%'
      },
      {
        key: 'currency',
        header: 'Currency',
        render: (row: any) => row.store_currency_code || '—',
        width: '10%'
      }
    ],
    []
  );

  return (
    <Wrap>
      <FilterRow>
        <SelectDropdown
          options={wineOptions}
          value={wine}
          onChange={setWine}
          placeholder="Wine"
        />
        <SelectDropdown
          options={vintageOptions}
          value={vintage}
          onChange={setVintage}
          placeholder="Vintage"
        />
      </FilterRow>

      <KpiStrip items={kpiItems} />

      {/* Price history */}
      <Section>
        <SectionTitle>Price History</SectionTitle>
        <DataTable
          columns={historyColumns}
          data={filteredHistory}
          emptyMessage={isLoading ? 'Loading...' : 'No price history data'}
        />
      </Section>

      {/* Seller details */}
      <Section>
        <SectionTitle>Seller Details</SectionTitle>
        <DataTable
          columns={storeColumns}
          data={filteredStores}
          emptyMessage={isLoading ? 'Loading...' : 'No seller data'}
        />
      </Section>
    </Wrap>
  );
};

// --- Styles ---

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const Section = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  padding: 20px;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-sans-bold);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
  color: var(--ink);
`;

const MarginCell = styled.span<{ $low: boolean }>`
  font-weight: 500;
  color: ${({ $low }) => ($low ? 'var(--terracotta)' : 'var(--olive)')};
`;
